import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ReactNode } from 'react';
import { styled, Stack, Box } from '@/theme/index';
import { Supertitle } from './Supertitle/Supertitle';
import {
  SectionContent,
  SectionNoContent,
  TitleInfo,
  PremiumTag,
} from './components';

export const ReportSectionStyled = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.customCard?.dark || '#fff',
  marginBottom: theme.spacing(2.5),
  width: '100%',
  position: 'relative',
  padding: theme.spacing(2.5),
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 50%)',
  maxWidth: '100vw',
}));

export const ReportSectionTopBar = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '0.25rem',
  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: theme.palette.reportSectionSupertitleBackgroundColor?.main,
}));

export interface ReportSectionProps {
  children?: ReactNode;
  FooterComponent?: ReactNode;
  showPremiumTag?: boolean;
  title?: string;
  titleName?: string;
  preTitleIcon?: ReactNode | null;
  hideSectionOptions?: boolean;
  sectionDescription?: string | string[]; // if array is passed each string will be separated by a line break
  hasContent: boolean;
  personName?: string;
  propertyAddress?: string;
  sectionId: string;
  customSectionButton?: ReactNode;
  customTitleStyle?: object;
  spacing?: number;
  howItWorks?: JSX.Element;
  namesAncestry?: boolean;
  permalink?: string;
  relatedProperties?: boolean;
  customEmptyText?: ReactNode;
  supertitleText?: string;
  showTopBar?: boolean;
}

export function ReportSection({
  children,
  FooterComponent,
  showPremiumTag,
  title,
  titleName,
  preTitleIcon,
  hideSectionOptions,
  sectionDescription,
  hasContent,
  personName,
  propertyAddress,
  sectionId,
  customSectionButton,
  customTitleStyle,
  spacing = 2,
  howItWorks,
  namesAncestry,
  permalink,
  relatedProperties,
  customEmptyText,
  supertitleText,
  showTopBar,
  ...rest
}: ReportSectionProps) {
  const isInReportSectionHeaderVariation = useFeatureIsOn('bump-3961');

  return (
    <>
      {isInReportSectionHeaderVariation && supertitleText && (
        <Supertitle text={supertitleText} />
      )}
      <ReportSectionStyled id={sectionId} {...rest}>
        {isInReportSectionHeaderVariation && showTopBar && (
          <ReportSectionTopBar />
        )}
        {showPremiumTag && <PremiumTag />}
        {(title || titleName) && (
          <TitleInfo
            title={title}
            titleName={titleName}
            preTitleIcon={preTitleIcon}
            hideSectionOptions={hideSectionOptions}
            customSectionButton={customSectionButton}
            customTitleStyle={customTitleStyle}
            sectionId={sectionId}
          />
        )}
        <Stack spacing={spacing}>
          {hasContent ? (
            <SectionContent
              children={children}
              sectionId={sectionId}
              sectionDescription={sectionDescription}
            />
          ) : (
            <SectionNoContent
              permalink={permalink}
              personName={personName}
              propertyAddress={propertyAddress}
              title={title}
              howItWorks={howItWorks}
              namesAncestry={namesAncestry}
              relatedProperties={relatedProperties}
              customEmptyText={customEmptyText}
            />
          )}
          {FooterComponent}
        </Stack>
      </ReportSectionStyled>
    </>
  );
}
